// Sidebar del portal Credit (Admin SureKeep / SOFOM) — fuente: mockup portal-admin-sk.html.
// Slugs/rutas en inglés; labels = copy visible (es-MX) tal cual el mock.
import { linkOptions } from '@tanstack/react-router';
import {
    AlertCircle,
    AlertTriangle,
    ArrowLeftRight,
    Coins,
    DollarSign,
    FileText,
    Flag,
    HandCoins,
    Home,
    Landmark,
    LineChart,
    ListFilter,
    MessageSquare,
    Percent,
    ShieldAlert,
    SlidersHorizontal,
    Smartphone,
    Star,
    Sun,
    UserCheck,
    Users,
    Wrench,
} from 'lucide-react';
import type { AppShellNavGroup } from '@/shared/patterns/AppShell';
import { COHORTS_PERMISSIONS } from './cohorts/permissions';
import { COLLECTIONS_PERMISSIONS } from './collections/permissions';
import { CUSTOMER_MESSAGES_PERMISSIONS } from './customerMessages/permissions';
import { INSTALLMENTS_PERMISSIONS } from './installments/permissions';
import { MDM_PERMISSIONS } from './mdm/permissions';
import { MODELIA_PERMISSIONS } from './modelia/permissions';
import { ORIGINATION_PERMISSIONS } from './origination/permissions';
import { POLICIES_PERMISSIONS } from './policies/permissions';
import { PORTFOLIO_PERMISSIONS } from './portfolio/permissions';
import { PROMOTIONS_PERMISSIONS } from './promotions/permissions';
import { SEGMENTS_PERMISSIONS } from './segments/permissions';
import { RECONCILIATION_PERMISSIONS } from './reconciliation/permissions';
import { TRANSFER_NOTE_PERMISSIONS } from '../transferNotes/permissions';

export const CREDIT_PORTAL_TITLE = 'Portal Admin SureKeep';

const section = (slug: string) =>
    linkOptions({ to: '/credit/$section', params: { section: slug } });

export const creditNav: AppShellNavGroup[] = [
    {
        items: [
            {
                label: 'Home',
                icon: Home,
                link: linkOptions({ to: '/credit' }),
                exact: true,
                permission: 'loan.admin.home.read',
            },
        ],
    },
    {
        label: 'Clientes',
        items: [
            {
                slug: 'customers',
                label: 'Clientes',
                icon: Users,
                link: linkOptions({ to: '/credit/customers' }),
                status: 'partial',
            },
            {
                slug: 'segments',
                label: 'Segmentos',
                pageTitle: 'Segmentos · grupos de clientes con tratamiento base diferenciado',
                icon: UserCheck,
                link: linkOptions({ to: '/credit/segments' }),
                permission: SEGMENTS_PERMISSIONS.read,
                status: 'connected',
            },
            {
                slug: 'cohorts',
                label: 'Cohorts',
                pageTitle: 'Cohorts · condiciones sobre los datos del cliente para armar segmentos',
                icon: ListFilter,
                link: linkOptions({ to: '/credit/cohorts' }),
                permission: COHORTS_PERMISSIONS.read,
                status: 'connected',
            },
        ],
    },
    {
        label: 'Cartera',
        items: [
            {
                slug: 'portfolio',
                label: 'Cartera',
                pageTitle: 'Cartera · expediente de cada crédito del tenant',
                icon: LineChart,
                link: linkOptions({ to: '/credit/portfolio' }),
                permission: PORTFOLIO_PERMISSIONS.readCredit,
                // La ficha del crédito pega al silo; el listado de la tabla todavía no tiene endpoint.
                status: 'partial',
            },
            {
                slug: 'installments',
                label: 'Cobranza',
                pageTitle: 'Cobranza · cuotas de toda la cartera, de la más vencida a la más nueva',
                icon: HandCoins,
                link: linkOptions({ to: '/credit/installments' }),
                permission: INSTALLMENTS_PERMISSIONS.readCredit,
                // El listado de cuotas y el lote sobre sus equipos pegan al silo. El nivel, el
                // nombre del cliente y el último mensaje no tienen fuente, y cuatro filtros tampoco.
                status: 'partial',
            },
            {
                slug: 'collections',
                label: 'Asignación de cartera',
                pageTitle: 'Cobranza · asignación de cartera entre cobradores',
                icon: Flag,
                link: linkOptions({ to: '/credit/collections' }),
                permission: COLLECTIONS_PERMISSIONS.readAssignments,
                // Carga por cobrador, asignaciones, reasignar, redistribuir y reglas pegan al silo.
                // La cobranza en sí (promesas de pago, gestiones, contacto) no tiene lambda todavía.
                status: 'partial',
            },
            {
                slug: 'exceptions',
                label: 'Excepciones',
                icon: AlertTriangle,
                link: section('exceptions'),
            },
            {
                slug: 'credit-fraud',
                label: 'Fraude crediticio',
                icon: ShieldAlert,
                link: section('credit-fraud'),
            },
            {
                slug: 'provisions',
                label: 'Provisiones',
                icon: Landmark,
                link: section('provisions'),
            },
            {
                slug: 'reconciliation',
                label: 'Conciliación',
                pageTitle: 'Conciliación · matriz anual y partidas del motor',
                icon: FileText,
                link: linkOptions({ to: '/credit/reconciliation' }),
                permission: [
                    RECONCILIATION_PERMISSIONS.definitionRead,
                    RECONCILIATION_PERMISSIONS.itemRead,
                ],
                status: 'connected',
            },
            {
                slug: 'transfers',
                label: 'Transferencias',
                pageTitle: 'Transferencias · notas con FSA y Luga',
                icon: ArrowLeftRight,
                link: linkOptions({ to: '/credit/transfers' }),
                permission: TRANSFER_NOTE_PERMISSIONS.read,
                status: 'connected',
            },
        ],
    },
    {
        label: 'Análisis',
        items: [
            {
                slug: 'origination',
                label: 'Originación',
                pageTitle: 'Originación · volumen, captura del mercado y ranking por tienda',
                icon: DollarSign,
                link: linkOptions({ to: '/credit/origination' }),
                permission: ORIGINATION_PERMISSIONS.viewAnalytics,
                // Tendencia, los dos indicadores con dato y el ranking pegan al silo; la aprobación
                // del motor, la cosecha, la conversión y los rechazos no existen todavía.
                status: 'partial',
            },
            {
                slug: 'calibration',
                label: 'Calibración',
                icon: AlertCircle,
                link: section('calibration'),
            },
            // El slug sigue siendo `analysis` porque la pantalla del mock es
            // `portal-admin-sk-analisis.html`; su item del sidebar se llama «Salud».
            { slug: 'analysis', label: 'Salud', icon: LineChart, link: section('analysis') },
            {
                slug: 'profitability',
                label: 'Rentabilidad',
                icon: Coins,
                link: section('profitability'),
            },
        ],
    },
    {
        label: 'Configuración',
        items: [
            {
                slug: 'policies',
                label: 'Políticas',
                pageTitle: 'Políticas · reglas base y especiales por segmento',
                icon: Wrench,
                link: linkOptions({ to: '/credit/policies' }),
                permission: POLICIES_PERMISSIONS.read,
                // Planes conectados a loan-offerings; Especiales y Auditoría mock (F2/F3).
                status: 'partial',
            },
            {
                slug: 'promotions',
                label: 'Promociones',
                pageTitle: 'Promociones · palancas del motor',
                icon: Star,
                link: linkOptions({ to: '/credit/promotions' }),
                permission: PROMOTIONS_PERMISSIONS.read,
                // CRUD, cambios de estado y bitácora pegan al silo. El cruce con las promos de
                // VentasLuga no se construyó: esas promos no existen como entidad en ningún repo.
                status: 'partial',
            },
            {
                slug: 'customer-messages',
                label: 'Mensajes al cliente',
                pageTitle: 'Mensajes al cliente · copy, canales y disparo de cada plantilla',
                icon: MessageSquare,
                link: linkOptions({ to: '/credit/customer-messages' }),
                // Los dos: la pantalla lee las plantillas Y los parámetros donde vive la ficha.
                permission: [
                    CUSTOMER_MESSAGES_PERMISSIONS.templateRead,
                    CUSTOMER_MESSAGES_PERMISSIONS.parameterRead,
                ],
                status: 'connected',
            },
            {
                slug: 'financial-parameters',
                label: 'Parámetros financieros',
                icon: Percent,
                link: section('financial-parameters'),
            },
            {
                slug: 'operacion-retail',
                label: 'Bases Operativas',
                icon: SlidersHorizontal,
                link: linkOptions({ to: '/seccion/$key', params: { key: 'operacion-retail' } }),
                permission: 'retail.retailer.list',
                status: 'connected',
            },
        ],
    },
    {
        label: 'Sistemas',
        items: [
            {
                slug: 'mdm',
                label: 'Control MDM',
                pageTitle: 'Control MDM · estado y operación de los equipos financiados',
                crumb: `${CREDIT_PORTAL_TITLE} · Sistemas`,
                icon: Smartphone,
                link: linkOptions({ to: '/credit/mdm' }),
                permission: MDM_PERMISSIONS.viewFleet,
                // La flota, la bitácora y lock/unlock/refresh pegan al silo; el resto del catálogo
                // de acciones del proveedor sigue sin sistema que lo ejecute.
                status: 'partial',
            },
            {
                slug: 'modelia',
                label: 'Modelia',
                pageTitle: 'Modelia · scoring de crédito',
                icon: Sun,
                link: linkOptions({ to: '/credit/modelia' }),
                permission: MODELIA_PERMISSIONS.viewEvaluations,
                // Las evaluaciones y los parámetros del modelo pegan al silo; la salud del
                // proveedor y el audit log del connector siguen sin endpoint que los publique.
                status: 'partial',
            },
        ],
    },
    {
        label: 'Reportes',
        items: [
            {
                slug: 'reports',
                label: 'Catálogo de reportes',
                pageTitle: 'Reportes · catálogo operativo y regulatorio',
                icon: FileText,
                link: linkOptions({ to: '/credit/reports' }),
                // Catálogo y log pegan al motor de reportes; sin servicio, la pantalla lo dice.
                status: 'partial',
            },
        ],
    },
];
